import { NonNullish } from "@/flokit/utils/comparison/non-nullish";
import { printDevError } from "@/flokit/utils/debug/print-dev-error";
import { cloneDeep } from "lodash";
import {
  JobAppAddressInformationRequest,
  JobAppDocumentUploadRequest,
  JobAppPersonalInformationRequest,
  JobAppQuestionAnswerRequest,
  JobAppSelfIdQuestionAnswerRequest,
  JobSectionRequestJobSectionType,
} from "modules/api-docs";
import { PublicJobAppQuery } from "modules/graphql.types";
import {
  DocumentDefinition,
  checkAllRequiredDocumentsArePresent,
} from "modules/self-apply/common/utils/validateJobAppDocuments";
import {
  DocumentFields,
  EducationHistoryFields,
  WorkHistoryFields,
} from "modules/self-apply/validation/candidate-self-apply";
import * as Yup from "yup";
import { ClientCandidateSelfApplyJobAppRequest } from "../../../../candidate/utils/handle-submit";
import { isAddressInfoRequired } from "./address-info";
import { SelfApplyOnlyNullish, SelfApplyOnlyNullishJobSection } from "./common";
import { customQuestionAnswersToRequests } from "./custom-questions";
import { SelfApplyOnlyDocumentSection } from "./documents";
import {
  educationHistoryToRequests,
  isEducationHistoryRequired,
} from "./education-history";
import { isSelfIdRequired, selfIdQuestionAnswersToRequests } from "./self-id";
import { isWorkHistoryRequired, workHistoryToRequests } from "./work-history";

type SelfApplyFieldSetsToClientCandidateSelfApplyJobAppRequestArgs = {
  // Other info
  jobSections: SelfApplyOnlyNullishJobSection[];
  existingJobApp: PublicJobAppQuery["publicCandidateJobApp"];
  isGovtContractor: boolean | undefined;
  orgFriendlyName: string;
  jobExtension: string;
  /**
   * Should be `true` if no documents to upload; otherwise `false`.
   */
  isFinalStep: boolean;
  // FieldSets
  personalInfoFields: JobAppPersonalInformationRequest;
  addressInfoFields: JobAppAddressInformationRequest;
  educationHistoryFields: EducationHistoryFields[];
  workHistoryFields: WorkHistoryFields[];
  customQuestionAnswersFields: JobAppQuestionAnswerRequest[];
  selfIdQuestionAnswersFields: JobAppSelfIdQuestionAnswerRequest[];
};
export function bundleFieldSetsToFormRequest(
  args: SelfApplyFieldSetsToClientCandidateSelfApplyJobAppRequestArgs,
): ClientCandidateSelfApplyJobAppRequest {
  const addressInfoRequired = isAddressInfoRequired(args.jobSections);
  const educationHistoryRequired = isEducationHistoryRequired(args.jobSections);
  const workHistoryRequired = isWorkHistoryRequired(args.jobSections);
  const selfIdQuestionsRequired = isSelfIdRequired(
    args.jobSections,
    args.isGovtContractor,
  );

  const educationHistoryRequests: ClientCandidateSelfApplyJobAppRequest["educationHistory"] =
    educationHistoryToRequests({ fields: args.educationHistoryFields });

  const workHistoryRequests: ClientCandidateSelfApplyJobAppRequest["workHistory"] =
    workHistoryToRequests({ fields: args.workHistoryFields });

  const applicationQuestions: ClientCandidateSelfApplyJobAppRequest["applicationQuestions"] =
    customQuestionAnswersToRequests({
      answers: args.customQuestionAnswersFields,
      existingAnswers: args.existingJobApp?.answers ?? [],
    });

  const selfIdQuestionAnswers: ClientCandidateSelfApplyJobAppRequest["selfIdQuestionAnswers"] =
    selfIdQuestionAnswersToRequests({
      answers: args.selfIdQuestionAnswersFields,
      existingAnswers: args.existingJobApp?.selfIdAnswers ?? [],
    });

  const clientRequest: ClientCandidateSelfApplyJobAppRequest & {
    isFinalStep: boolean;
  } = {
    orgFriendlyName: args.orgFriendlyName,
    jobExtension: args.jobExtension,
    personalInfo: args.personalInfoFields,
    addressInfoRequired,
    addressInfo: args.addressInfoFields,
    educationHistory: educationHistoryRequests,
    educationHistoryRequired,
    workHistory: workHistoryRequests,
    workHistoryRequired,
    applicationQuestions,
    selfIdQuestionsRequired,
    selfIdQuestionAnswers,
    isFinalStep: args.isFinalStep,
  };

  return clientRequest;
}

type ValidateDocumentsArgs = {
  // jobSections: NonNullable<
  //   NonNullable<PublicJobQuery>["publicJob"]
  // >["jobSections"];
  // Not sure how else to extricate this from the GraphQL query type/whether we want to do that
  jobSections?: SelfApplyOnlyDocumentSection[];
  fields: DocumentFields[];
};
/**
 * @throws if not all required documents are present.
 */
export function validateDocuments({
  jobSections,
  fields,
}: ValidateDocumentsArgs) {
  if (!jobSections) {
    return;
  }

  const candidateDocumentsSection = jobSections?.find(
    (jobSection) =>
      jobSection?.jobSectionType ===
      JobSectionRequestJobSectionType.CANDIDATE_DOCUMENTS,
  );

  // If the "Documents" section has any documents marked as required, then the section is automatically required regardless of jobSection#isRequired

  const documentDefinitions: DocumentDefinition[] = (
    candidateDocumentsSection?.jobCandidateDocuments ?? []
  )
    .map((candidateDocument) => {
      const orgDocId = candidateDocument?.orgDocId;
      if (!orgDocId) {
        return null;
      }

      const definition: DocumentDefinition = {
        isRequired: !!candidateDocument?.isRequired,
        orgDocId,
      };

      return definition;
    })
    .filter(NonNullish);

  if (!checkAllRequiredDocumentsArePresent(documentDefinitions, fields)) {
    throw new Error("Missing Documents");
  }
}

export type ValidateSelfApplyJobApplicationOnError = (error: {
  type: "FORM_VALIDATION" | "DOCUMENT_VALIDATION";
}) => void;

type ValidateJobApplication<
  TForm extends { isFinalStep?: SelfApplyOnlyNullish<boolean> },
> = {
  /**
   * The non-document sections of the form.
   */
  formFields: TForm;
  /**
   * Schema combining the non-document sections of the form.
   */
  formSchema: Yup.SchemaOf<unknown>; // 'unknown' because it doesn't seem like the schema we want to use exactly matches the request
  documentFields: DocumentFields[];
  /**
   * Used to validate documents
   */
  jobSections: ValidateDocumentsArgs["jobSections"];
  onSuccess?: () => void;
  onError?: ValidateSelfApplyJobApplicationOnError;
  onSettled?: () => void;
};
/**
 * Validates both the document and non-document sections of the job application.
 */
export function validateSelfApplyJobApplication<
  TForm extends { isFinalStep?: SelfApplyOnlyNullish<boolean> },
>(args: ValidateJobApplication<TForm>) {
  try {
    args.formSchema.validateSync(args.formFields);
  } catch (err) {
    printDevError(err);

    args.onError?.({ type: "FORM_VALIDATION" });
    args.onSettled?.();
    return;
  }

  try {
    validateDocuments({
      fields: args.documentFields,
      jobSections: args.jobSections,
    });
  } catch (err) {
    printDevError(err);

    args.onError?.({ type: "DOCUMENT_VALIDATION" });
    args.onSettled?.();
    return;
  }

  args.onSuccess?.();
  args.onSettled?.();
  return;
}

export type UploadSelfApplyJobApplicationOnError = (
  error:
    | { type: "FORM_UPLOAD" }
    | {
        type: "DOCUMENT_UPLOAD";
        /**
         * The id of the document whose upload failed. `undefined` likely indicates a FE bug in which we're failing to provide the document id.
         */
        orgDocId: number | undefined;
      },
) => void;

type UploadJobApplicationArgs<
  TFormRequest extends { isFinalStep?: SelfApplyOnlyNullish<boolean> },
> = {
  formRequest: TFormRequest;
  /**
   * Uploaded in order provided.
   */
  documentRequests: JobAppDocumentUploadRequest[];
  /**
   * @returns the `jobAppId`
   */
  uploadForm: (formRequest: TFormRequest) => Promise<number>;
  /**
   * Called on each provided document, in order provided.
   */
  uploadDocument: (
    documentRequest: JobAppDocumentUploadRequest,
  ) => Promise<void>;
  /**
   * Called before uploading begins.
   */
  onSubmit?: () => void;
  /**
   * Called once both the form and all documents have been uploaded successfully.
   */
  onSuccess?: (args: { submittedJobAppId: number }) => void;
  /**
   * Called when either uploading the form or uploading one of the documents fails.
   */
  onError?: UploadSelfApplyJobApplicationOnError;
  onSettled?: () => void;
};
/**
 * Uploads the non-document sections of the job application, followed by the uploaded documents in-order.
 *
 * @returns the id of the submitted job app.
 */
export async function uploadSelfApplyJobApplication<
  TForm extends { isFinalStep?: SelfApplyOnlyNullish<boolean> },
>(args: UploadJobApplicationArgs<TForm>) {
  args.onSubmit?.();

  // If no documents, form submission is the final step
  const submission = cloneDeep(args.formRequest);
  submission.isFinalStep = !args.documentRequests.length;

  // Submit form and get job app id
  let submittedJobAppId: number | null = null;
  try {
    submittedJobAppId = await args.uploadForm(submission);
  } catch (err) {
    printDevError(err);

    args.onError?.({ type: "FORM_UPLOAD" });

    return args.onSettled?.();
  }

  // Submit documents in order using id of submitted job app
  for (const document of args.documentRequests) {
    // Application upload failed, do not proceed
    if (submittedJobAppId === null) {
      continue;
    }

    try {
      document.jobAppId = submittedJobAppId;
      await args.uploadDocument(document);
    } catch (err) {
      printDevError(err);

      args.onError?.({
        type: "DOCUMENT_UPLOAD",
        orgDocId: document.orgcustomdocumentsId,
      });

      return args.onSettled?.();
    }
  }

  if (submittedJobAppId !== null) {
    args.onSuccess?.({ submittedJobAppId });
  }

  return args.onSettled?.();
}
